import { FC, createContext, useContext, useEffect, useState } from 'react';
import { CreateEstimateContextType } from 'containers/estimates/types';
import AddItemModal, { IAddItemModalConfig } from 'containers/invoices/create-invoice/components/AddItemModal';
import { If } from 'components';
import { useAppDispatch, useAppSelector } from 'hooks';
import { CreateEditEstimateNavEnum } from 'containers/estimates/constants'; // Changed from 'constant' to 'constants'
import { DraggableItemIcon, FormEditIcon } from 'assets/icons';
import FormCloseReactIcon from 'assets/react-icons/FormCloseReactIcon';
import Draggable from 'react-draggable';
import { formattedLineItemQty, getAmountWithCurrency } from 'utils/amountUtils';
import AddLineItemSectionWrapper from 'containers/invoices/create-invoice/components/AddLineItemSectionWrapper';
import AddInvoiceDiscountWrapper from 'containers/invoices/create-invoice/components/AddInvoiceDiscountWrapper';
import AddServiceFeeWrapper from 'containers/invoices/create-invoice/components/AddServiceFeeWrapper';
import AddServiceChargeWrapper from 'containers/invoices/create-invoice/components/AddServiceChargeWrapper';
import { LINE_ITEM_SOURCE } from 'constants/common';
import {
  EInvoiceCatalogElementType,
  EInvoiceModalType,
  EItemModalType,
  InvoiceDraftModalData
} from 'containers/invoices/create-invoice/types';
import {
  addEstimateLineItem,
  incrementEstimateLineItemQty,
  modifyEstimateLineItem,
  removeEstimateDiscountFormData,
  removeEstimateLineItem,
  removeEstimateServiceCharge,
  resetEstimateDraftData,
  selectAppliedEstimateLineItemLevelTaxes,
  selectCascadingRelatedEntityData,
  selectCurrentSurchargeInEstimates,
  selectEstimateDiscountFormData,
  selectEstimateDiscountSettings,
  selectEstimateFinalAmount,
  selectEstimateLineItemDraftModalData,
  selectEstimateLineItems,
  selectEstimateLineItemsTotal,
  selectEstimateSelectedServiceCharge,
  selectEstimateSubtotal,
  selectGlobalLineItemTaxesEstimatesSlice,
  selectGlobalServiceChargeTaxesInEstimateSlice,
  selectIsBackFromCreateTaxFromEstimatesSlice,
  selectIsServiceFeeEnabledFromEstimate,
  updateEstimateDiscountFormData,
  updateEstimateDraftData,
  updateIsBackFromCreateTaxInEstimatesSlice,
  updateIsServiceFeeEnabledInEstimates,
  updateLineItemsOrderInEstimatesSlice,
  updateSelectedEstimateServiceCharge
} from 'containers/estimates/estimatesSlice';
import { getAggregatedTaxInCurrencyWithSymbol } from 'containers/invoices/utils';
import { InvoiceStatus } from 'containers/invoices/invoices.model';

interface EstimateLineItemSectionProps {
  className?: string;
  isLoadingTaxesAndFees: boolean;
  context: React.Context<CreateEstimateContextType>;
}

const addItemModalConfigInitState: IAddItemModalConfig = {
  open: false,
  data: { id: 0, name: '', unitPrice: '', sku: '', description: '', selectedTaxes: [], quantity: '1' },
  editMode: false
};

export interface ILineItemsSectionContext {
  showAddFromCatalogModal: boolean;
  setShowAddFromCatalogModal: React.Dispatch<React.SetStateAction<boolean>>;
}

export const EstimateLineItemsSectionContext = createContext<ILineItemsSectionContext>({
  showAddFromCatalogModal: false,
  setShowAddFromCatalogModal: () => {
    // This is intentionally left empty
  }
});

const EstimateLineItemSection: FC<EstimateLineItemSectionProps> = ({ isLoadingTaxesAndFees, context }) => {
  const dispatch = useAppDispatch();
  const { selectedEstimateTab, setSelectedEstimateTab } = useContext<CreateEstimateContextType>(context);
  const [showAddFromCatalogModal, setShowAddFromCatalogModal] = useState<boolean>(false);
  const [addItemModalConfig, setAddItemModalConfig] = useState<IAddItemModalConfig>(addItemModalConfigInitState);

  const estimateLineItems = useAppSelector(selectEstimateLineItems);
  const lineItemsTotal = useAppSelector(selectEstimateLineItemsTotal);
  const estimateSubtotal = useAppSelector(selectEstimateSubtotal);
  const estimateFinalAmount = useAppSelector(selectEstimateFinalAmount);
  const discountFormData = useAppSelector(selectEstimateDiscountFormData);
  const discountSettings = useAppSelector(selectEstimateDiscountSettings);
  const currentSurcharge = useAppSelector(selectCurrentSurchargeInEstimates);
  const selectedServiceCharge = useAppSelector(selectEstimateSelectedServiceCharge);
  const appliedLineItemLevelTaxes = useAppSelector(selectAppliedEstimateLineItemLevelTaxes);
  const { relatedInvoiceStatus } = useAppSelector(selectCascadingRelatedEntityData);

  const [items, setItems] = useState(estimateLineItems);

  useEffect(() => {
    setItems(estimateLineItems);
  }, [estimateLineItems]);

  const isTabSelected = selectedEstimateTab === CreateEditEstimateNavEnum.LINE_ITEMS;

  const onFocus = () => {
    setSelectedEstimateTab(CreateEditEstimateNavEnum.LINE_ITEMS);
  };

  const onRemoveLineItem = lineItem => {
    dispatch(removeEstimateLineItem(lineItem));
  };

  const onEditLineItem = lineItem => {
    const itemData = {
      id: +lineItem.id,
      catalogueLineItemId: lineItem.catalogueLineItemId,
      name: lineItem.itemDescription,
      unitPrice: lineItem.unitCost,
      unitCost: lineItem.originalUnitCost,
      markupPercentage: lineItem.markupPercentage,
      sku: lineItem.sku,
      description: lineItem.additionalDetails,
      selectedTaxes: lineItem?.taxes ?? [],
      quantity: lineItem.quantity
    };
    setAddItemModalConfig({ open: true, data: itemData, editMode: true });
  };

  const onSaveLineItemsClick = lineItemData => {
    dispatch(addEstimateLineItem(lineItemData.invoiceLineItem));
    resetItemModalConfig();
  };

  const onModifyLineItemClick = lineItem => {
    dispatch(modifyEstimateLineItem(lineItem));
    resetItemModalConfig();
  };

  const resetItemModalConfig = () => {
    setAddItemModalConfig(addItemModalConfigInitState);
  };

  const handleDragStart = (e, data, index) => {
    const draggedItemDiv = document.getElementById(`draggable-line-item-div-${index}`);
    if (draggedItemDiv) {
      draggedItemDiv.style.zIndex = '10';
    }
  };

  const handleDrag = (e, data, index) => {
    const itemDivHeight = getHeightOfDiv(index);
    let newIndex = index + Math.round(data.lastY / itemDivHeight);

    if (newIndex < 0) {
      newIndex = 0;
    } else if (newIndex >= items.length) {
      newIndex = items.length - 1;
    }

    if (index !== newIndex) {
      const newItems = [...items];
      const movedItem = newItems.splice(index, 1)[0];
      newItems.splice(newIndex, 0, movedItem);

      setItems(newItems);
      dispatch(updateLineItemsOrderInEstimatesSlice(newItems));
    }

    const draggedItemDiv = document.getElementById(`draggable-line-item-div-${index}`);
    if (draggedItemDiv) {
      const zIndex = items.length - newIndex;
      draggedItemDiv.style.zIndex = zIndex.toString();
    }
  };

  const getHeightOfDiv = (index: number) => {
    const div = document.querySelector(`#draggable-line-item-div-${index}`);
    if (div) {
      const elementRect = div.getBoundingClientRect();
      return elementRect.height;
    }
    return 0;
  };

  const handleIncrementLineItemQty = itemToBeAdded => {
    dispatch(incrementEstimateLineItemQty(itemToBeAdded));
  };

  const handleSaveDiscount = newDiscountFormData => {
    dispatch(updateEstimateDiscountFormData(newDiscountFormData));
  };

  const onRemoveDiscount = () => {
    dispatch(removeEstimateDiscountFormData());
  };

  const handleIsServiceFeeEnabled = newIsServiceFeeEnabled => {
    dispatch(updateIsServiceFeeEnabledInEstimates(newIsServiceFeeEnabled));
  };

  const handleUpdateSelectedServiceCharge = serviceCharge => {
    dispatch(updateSelectedEstimateServiceCharge(serviceCharge));
  };

  const handleRemoveSelectedServiceCharge = () => {
    dispatch(removeEstimateServiceCharge());
  };

  const handleUpdateEstimateModalData = (estimateItemModalDraft: InvoiceDraftModalData) => {
    dispatch(updateEstimateDraftData(estimateItemModalDraft));
  };

  const isBackFromCreateTaxRoute = useAppSelector(selectIsBackFromCreateTaxFromEstimatesSlice);
  const selectedEstimateDraftData = useAppSelector(selectEstimateLineItemDraftModalData);

  const showDraftEditItemModal = () => {
    const { modalConfig } = selectedEstimateDraftData ?? {};
    const itemModalConfig: IAddItemModalConfig = {
      open: true,
      data: modalConfig?.data ?? addItemModalConfigInitState,
      editMode: modalConfig?.editMode ?? false,
      draftFlow: modalConfig?.draftFlow
    };
    if (selectedEstimateDraftData.invoiceModalType === EInvoiceModalType.EDIT_ITEM_MODAL) {
      setAddItemModalConfig({
        data: itemModalConfig.data,
        open: true,
        editMode: true,
        draftFlow: { isDraftFlow: true, saveToCatalog: false }
      });

      if (isBackFromCreateTaxRoute) {
        handleOnBackFromCreateTax();
      }
    }
  };

  const handleOnBackFromCreateTax = () => {
    dispatch(resetEstimateDraftData());
    dispatch(updateIsBackFromCreateTaxInEstimatesSlice(false));
  };

  useEffect(() => {
    if (isBackFromCreateTaxRoute) {
      if (
        selectedEstimateDraftData &&
        selectedEstimateDraftData?.invoiceCatalogElementType === EInvoiceCatalogElementType.ITEM &&
        selectedEstimateDraftData?.invoiceModalType === EInvoiceModalType.EDIT_ITEM_MODAL
      ) {
        showDraftEditItemModal();
      }
    }
  }, [isBackFromCreateTaxRoute, selectedEstimateDraftData]);

  const isEditDisabled = relatedInvoiceStatus && relatedInvoiceStatus !== InvoiceStatus.PENDING;

  return (
    <EstimateLineItemsSectionContext.Provider value={{ showAddFromCatalogModal, setShowAddFromCatalogModal }}>
      <div onFocus={onFocus}>
        <div className={`mb-2 text-xl font-bold ${isTabSelected ? 'text-primaryBtn' : 'text-heading'}`}>
          Line Items
          <span className="text-error">*</span>
        </div>
        <div
          className={`flex flex-col rounded-md border px-8 py-6 ${
            isTabSelected ? 'border-primaryBtn' : 'border-borderGray'
          }`}>
          <If condition={items.length > 0}>
            <div className="mb-4 flex flex-row text-sbase font-semibold text-primaryText">
              <div className="w-1/2 min-w-[60px] whitespace-nowrap">Item</div>
              <div className="w-[15%] min-w-[60px] whitespace-nowrap">Unit Price</div>
              <div className="w-[15%] min-w-[60px] whitespace-nowrap text-center">Quantity</div>
              <div className="w-[20%] min-w-[60px] whitespace-nowrap pr-5 text-right">Total</div>
            </div>
          </If>
          <div className="space-y-5">
            <div className="relative space-y-5">
              {items?.map((lineItem, index) => (
                <Draggable
                  key={index}
                  handle=".draggableContainer"
                  axis="y"
                  position={{ x: 0, y: 0 }}
                  onStart={(e, data) => handleDragStart(e, data, index)}
                  onStop={(e, data) => handleDrag(e, data, index)}
                  bounds={'parent'}>
                  <div
                    id={`draggable-line-item-div-${index}`}
                    className={`draggableContainer relative flex min-h-[85px] w-full cursor-move flex-row
                    rounded border border-borderGray bg-white py-4`}>
                    <div className="flex w-1/2 min-w-[60px] flex-col gap-3 pl-5 pr-2">
                      <div
                        onClick={() => onEditLineItem(lineItem)}
                        className="line-clamp-8 cursor-pointer break-words 
                        text-17px font-semibold text-secondary">
                        {lineItem?.itemDescription}
                      </div>
                      <div className="whitespace-pre-line text-sbase font-normal text-primaryText line-clamp-3">
                        {lineItem?.additionalDetails}
                      </div>
                    </div>
                    <div className="w-[15%] min-w-[60px] truncate text-17px font-semibold text-heading">
                      {getAmountWithCurrency(lineItem?.unitCost, '$')}
                    </div>
                    <div
                      className="flex w-[15%] min-w-[60px] justify-center 
                      truncate text-17px font-semibold text-primaryText">
                      <div className="truncate">{formattedLineItemQty({ qtyString: lineItem?.quantity })}</div>
                    </div>
                    <div className="flex w-[20%] min-w-[60px] flex-col gap-3 pr-5">
                      <div className="truncate text-right text-17px font-semibold text-heading">
                        {getAmountWithCurrency(lineItem?.total)}
                      </div>
                      <If condition={lineItem?.taxes?.length > 0}>
                        <div className="text-right text-sbase font-normal text-primaryText">
                          {lineItem?.taxes?.length === 1 ? '+1 tax' : `+${lineItem?.taxes?.length} taxes`}
                        </div>
                      </If>
                    </div>
                    <If condition={!isEditDisabled}>
                      <FormCloseReactIcon
                        id={`remove-line-item-${index}`}
                        onClick={() => onRemoveLineItem(lineItem)}
                        className="absolute top-3 right-[-25px] shrink-0 cursor-pointer"
                      />
                      <FormEditIcon
                        id={`edit-line-item-${index}`}
                        onClick={() => onEditLineItem(lineItem)}
                        className="absolute bottom-3 right-[-25px] mt-3 shrink-0 cursor-pointer"
                      />
                    </If>
                    <DraggableItemIcon
                      className="absolute -left-5 top-1/2 shrink-0 
                    -translate-y-2/4 cursor-move 1439px:-left-4"
                    />
                  </div>
                </Draggable>
              ))}
            </div>

            <AddLineItemSectionWrapper
              onSaveLineItemClick={onSaveLineItemsClick}
              onModifyLineItemClick={onModifyLineItemClick}
              context={EstimateLineItemsSectionContext}
              from={LINE_ITEM_SOURCE.ESTIMATES}
              selectLineItems={selectEstimateLineItems}
              incrementLineItemQty={handleIncrementLineItemQty}
              selectGlobalLineItemTaxes={selectGlobalLineItemTaxesEstimatesSlice}
              selectIsBackFromCreateTax={selectIsBackFromCreateTaxFromEstimatesSlice}
              selectInvoiceDraftData={selectEstimateLineItemDraftModalData}
              onBackFromCreateTax={handleOnBackFromCreateTax}
              updateInvoiceModalData={handleUpdateEstimateModalData}
            />

            <div className="ml-auto w-1/2 min-w-[405px] rounded-md border border-x-borderGray">
              <If condition={discountSettings?.enabled}>
                <AddInvoiceDiscountWrapper
                  selectedDiscount={discountFormData}
                  isAddDiscountDisabled={items.length === 0 || isEditDisabled}
                  isEditInvoiceFlow={false}
                  lineItemsTotalCost={lineItemsTotal}
                  handleRemoveDiscount={onRemoveDiscount}
                  onSaveDiscount={handleSaveDiscount}
                />
              </If>

              <div
                className="flex flex-row justify-between border-b
               border-borderGray p-5 text-17px font-semibold text-heading">
                <div>Subtotal</div>
                <div className="w-0 grow break-words text-right">{getAmountWithCurrency(estimateSubtotal)}</div>
              </div>

              <If condition={currentSurcharge?.enabled}>
                <AddServiceFeeWrapper
                  serviceFee={currentSurcharge}
                  isLoadingTaxesAndFees={isLoadingTaxesAndFees}
                  isEdit={false}
                  selectIsServiceFeeEnabled={selectIsServiceFeeEnabledFromEstimate}
                  onSaveIsServiceFeeEnabled={handleIsServiceFeeEnabled}
                  isEditDisabled={isEditDisabled}
                  source={LINE_ITEM_SOURCE.ESTIMATES}
                />
              </If>

              <AddServiceChargeWrapper
                isAddServiceChargeDisabled={items.length === 0 || isEditDisabled}
                selectedServiceCharge={selectedServiceCharge}
                onRemoveSelectedServiceCharge={handleRemoveSelectedServiceCharge}
                updateSelectedServiceCharge={handleUpdateSelectedServiceCharge}
                selectGlobalLineItemTaxes={selectGlobalServiceChargeTaxesInEstimateSlice}
                selectIsBackFromCreateTax={selectIsBackFromCreateTaxFromEstimatesSlice}
                selectInvoiceDraftData={selectEstimateLineItemDraftModalData}
                onBackFromCreateTax={handleOnBackFromCreateTax}
                updateInvoiceModalData={handleUpdateEstimateModalData}
              />

              <If condition={appliedLineItemLevelTaxes?.length > 0}>
                <div className="flex flex-col border-b border-borderGray p-5">
                  <div className="flex flex-col gap-[13px]">
                    {appliedLineItemLevelTaxes.map(aggregatedItemTax => (
                      <div className="flex flex-row items-start" key={aggregatedItemTax.id}>
                        <div className="w-1/2 break-words text-17px text-heading ">{aggregatedItemTax.name}</div>
                        <div className="w-1/2 break-words text-right text-17px font-normal text-heading">
                          {getAggregatedTaxInCurrencyWithSymbol(
                            estimateLineItems,
                            aggregatedItemTax,
                            selectedServiceCharge
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </If>

              <div
                className="flex h-[50px] flex-row items-center 
                justify-between bg-borderGray px-[22px] text-xl font-bold text-heading">
                <div>Total Amount</div>
                <div className="w-0 grow break-words text-right">{getAmountWithCurrency(estimateFinalAmount)}</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {addItemModalConfig.open && (
        <AddItemModal
          title="Edit Item"
          isEdit={true}
          onSaveLineItemClick={onSaveLineItemsClick}
          onModifyLineItemClick={onModifyLineItemClick}
          config={addItemModalConfig}
          onClose={resetItemModalConfig}
          resetItemModalConfig={resetItemModalConfig}
          isEditInvoiceFlow={false}
          modalType={EItemModalType.EDIT_ITEM_MODAL}
          selectGlobalLineItemTaxes={selectGlobalLineItemTaxesEstimatesSlice}
          updateInvoiceModalData={handleUpdateEstimateModalData}
        />
      )}
    </EstimateLineItemsSectionContext.Provider>
  );
};

export default EstimateLineItemSection;
